<script lang="ts" setup>
import { computed, onMounted, provide, ref, watch } from 'vue'
import {
  type MessageWithReply,
  extractUserId,
  extractUserName,
  getReactionEmoji,
  isMySender,
  useChatStore,
  useChatStoreCore,
  useCurrentUser,
  useMessageGrouping,
  useNotifications,
  usePhotoSwipe,
} from '../chatModules'
import ChatLogSkeleton from './chat-log/ChatLogSkeleton.vue'
import DateSeparator from './chat-log/DateSeparator.vue'
import MessageGroup from './chat-log/MessageGroup.vue'
import ReactionPicker from './chat-log/ReactionPicker.vue'
import MessageMenu from './chat-log/MessageMenu.vue'
import MessageReaders from './chat-log/MessageReaders.vue'
import PinnedMessagesBar from './PinnedMessagesBar.vue'
import ConfirmChatDialog from './dialogs/ConfirmChatDialog.vue'
import ForwardMessageDialog from './dialogs/ForwardMessageDialog.vue'
import { useMessageActions } from '@/modules/chat-matrix-fast/composables/useMessageActions'
import { canSelectMessageForForward } from '@/modules/chat-matrix-fast/composables/useMessageSelection'
import { useMessageHoverUi } from '@/modules/chat-matrix-fast/composables/useMessageHoverUi'
import { useChatLogReactions } from '@/modules/chat-matrix-fast/composables/useChatLogReactions'
import { usePinnedMessages } from '@/modules/chat-matrix-fast/composables/usePinnedMessages'
import { useActiveChatMessages } from '@/modules/chat-matrix-fast/composables/useActiveChatMessages'
import {
  type ChatReactionContext,
  chatReactionContextKey,
} from '@/modules/chat-matrix-fast/composables/useChatReactionContext'
import { isGroupLikeChatType } from '@/modules/chat-matrix-fast/constants/chat'
import { isDialogChat } from '@/modules/chat-matrix-fast/types/ChatMessage'
import { emojiToReactionTypeId } from '@/modules/chat-matrix-fast/services/matrixReactionService'
import {
  messageHasMatrixRelationReply,
  resolveReplyToForMessage,
} from '@/modules/chat-matrix-fast/utils/replyPreview'

withDefaults(defineProps<{
  loadingOlderHistory?: boolean
}>(), {
  loadingOlderHistory: false,
})

interface Emits {
  (e: 'reply', message: MessageWithReply, senderId: number | string): void
  (e: 'quote-click', messageId: string | number, matrixEventId?: string): void
  (e: 'avatar-click', userId: string | number): void
  (
    e: 'start-edit',
    messageId: string | number,
    msgData: MessageWithReply,
  ): void
  (e: 'selection-change', payload: { active: boolean; count: number }): void
}

const emit = defineEmits<Emits>()

const store = useChatStore()
const chatStoreCore = useChatStoreCore()
const { showError } = useNotifications()

const currentUser = useCurrentUser(
  computed(() => store.activeChat?.chat || null),
)

const myId = computed(() => currentUser.id.value)
const matrixUserId = computed(() => currentUser.matrixUserId.value)

const profileUser = computed(() => {
  if (store.profileUser) return store.profileUser

  return {
    id: currentUser.id.value || 'unknown',
    fullName: currentUser.name.value || 'Пользователь',
    avatar: currentUser.avatar.value || undefined,
  }
})

const messages = useActiveChatMessages()

const currentChat = computed(() => store.activeChat?.chat)
const chatId = computed(() => currentChat.value?.id)
const chatType = computed(() => currentChat.value?.type)
const matrixRoomId = computed(() => chatStoreCore.activeMessageRoomId ?? '')
const coreMessages = computed(() => chatStoreCore.messages)
const {
  pinnedMessages,
  isPinning,
  isPinned,
  pinMessage,
  unpinMessage,
} = usePinnedMessages(matrixRoomId, coreMessages)

const canPinMessages = computed(() => store.canPinMessages())
const canForwardFromActiveChat = computed(() => store.canForwardFromActiveChat())
const canReplyInActiveChat = computed(() => chatType.value !== 'channel')
const canOpenProfileInActiveChat = computed(() => chatType.value !== 'channel')

const isPinDialogMode = computed<'dialog' | 'group'>(() => {
  const chat = currentChat.value
  if (chat && isDialogChat(chat as any)) {
    return 'dialog'
  }

  return 'group'
})

const pinPeerName = computed(() => store.activeChat?.contact?.fullName || '')

const pinActorName = computed(() => profileUser.value?.fullName || 'Участник')

const isLastMessageInChat = (item: any, index: number, allItems: any[]) => {
  let lastGroupIndex = -1
  for (let i = allItems.length - 1; i >= 0; i--) {
    if (allItems[i].kind === 'group') {
      lastGroupIndex = i
      break
    }
  }

  if (lastGroupIndex === -1) return false

  if (index !== lastGroupIndex) return false

  if (!item.group || !item.group.messages || item.group.messages.length === 0) {
    return false
  }

  return true
}

const messagesWithContent = computed(() => {
  return messages.value.filter(msg => {
    if (msg.is_system) {
      return true
    }

    return !!(
      msg.message
      || (msg.attachments && msg.attachments.length > 0)
      || msg.reply_to
      || messageHasMatrixRelationReply(msg)
    )
  })
})

const { groupedMessages } = useMessageGrouping(messagesWithContent)

const contact = computed(() => ({
  id: store.activeChat?.contact.id,
  avatar: store.activeChat?.contact.avatar,
  fullName: store.activeChat?.contact.fullName,
}))

const isMine = (senderId?: number | string) => {
  if (!senderId) return false

  return isMySender(senderId, myId.value || '', matrixUserId.value)
}

const hasReplyContext = (msgData: MessageWithReply): boolean => {
  return !!(msgData.reply_to || msgData.reply_to_id || messageHasMatrixRelationReply(msgData))
}

const getReplySenderName = (
  msgData: MessageWithReply,
  _currentSenderId: number | string,
) => {
  interface ReplyToData {
    created_by?: {
      id?: number | string
      full_name?: string
      user_name?: string
    }
    user_id?: number | string
    user?: { id?: number | string; full_name?: string; user_name?: string }
  }

  const replyData = (
    msgData.reply_to
    ?? resolveReplyToForMessage(chatStoreCore.messages, msgData)
  ) as ReplyToData | null

  if (!replyData) {
    return store.activeChat?.contact?.fullName || 'Неизвестный'
  }

  const replySenderId = extractUserId(replyData)

  const isReplyToMine
    = replySenderId && isMySender(replySenderId, myId.value || '', matrixUserId.value)

  if (isReplyToMine) return profileUser.value?.fullName || 'Вы'

  return (
    extractUserName(replyData)
    || store.activeChat?.contact?.fullName
    || 'Неизвестный'
  )
}

const {
  availableReactions,
  getGroupedReactions,
  resolveStoreMessageId,
  applyReactionChange,
  handleReactionClick,
  getActiveEmoji,
} = useChatLogReactions({
  messages,
  reactionProfile: computed(() => ({
    fullName: profileUser.value?.fullName,
    avatar: profileUser.value?.avatar ?? null,
  })),
})

const {
  hoveredMessageId,
  showPickerId,
  pickerPosition,
  showMenuId,
  menuPosition,
  onMessageEnter,
  onMessageLeave,
  closeReactionPicker,
  onTriggerEnter,
  onTriggerLeave,
  onPickerEnter,
  onPickerLeave,
  openMessageMenu,
  closeMessageMenu,
} = useMessageHoverUi({ resolveMessageId: resolveStoreMessageId })

const pickerActiveEmoji = computed(() => {
  const messageId = showPickerId.value
  if (messageId == null) {
    return null
  }

  return getActiveEmoji(messageId)
})

interface ReactionDisplay {
  type: 'image' | 'emoji'
  src?: string
  emoji?: string
}

const getReactionDisplay = (reaction: {
  icon?: string
  emoji?: string
}): ReactionDisplay => {
  const icon = reaction.icon || ''

  if (icon.includes('/') || icon.match(/\.(png|jpg|jpeg|gif|svg|webp)$/i)) {
    return { type: 'image', src: icon }
  }

  const emoji = getReactionEmoji(reaction)

  return { type: 'emoji', emoji }
}

const getMessageId = (msgData: MessageWithReply, senderId: number | string) => {
  return msgData.matrix_event_id ?? msgData.id ?? `${senderId}-${msgData.time}`
}

const lastMessageInChat = computed(() => {
  if (!groupedMessages.value || groupedMessages.value.length === 0) return null

  let lastGroup: any = null
  for (let i = groupedMessages.value.length - 1; i >= 0; i--) {
    if (groupedMessages.value[i].kind === 'group') {
      lastGroup = groupedMessages.value[i]
      break
    }
  }

  if (
    !lastGroup
    || !lastGroup.group
    || !lastGroup.group.messages
    || lastGroup.group.messages.length === 0
  ) {
    return null
  }

  const lastMessage
    = lastGroup.group.messages[lastGroup.group.messages.length - 1]

  const messageId = getMessageId(lastMessage, lastGroup.group.senderId)

  return {
    message: lastMessage,
    messageId,
    matrixEventId: lastMessage.matrix_event_id,
    senderId: lastGroup.group.senderId,
  }
})

const shouldShowReaders = computed(() => {
  if (store.isLoadingChat) return false

  const last = lastMessageInChat.value
  if (!last || !matrixRoomId.value || !last.matrixEventId) return false

  return isGroupLikeChatType(chatType.value) || chatType.value === 'event'
})

const onTriggerClick = async (messageId: string | number, event: MouseEvent) => {
  event.stopPropagation()

  try {
    await applyReactionChange(messageId, 1, '👍')
    closeReactionPicker()
  } catch (error) {
    showError(error, 'Set like reaction')
  }
}

const selectEmojiReaction = async (
  messageId: string | number,
  emoji: string,
) => {
  try {
    const reactionId = emojiToReactionTypeId(emoji, availableReactions.value)
    await applyReactionChange(messageId, reactionId, emoji)
    closeReactionPicker()
  } catch (error) {
    showError(error, 'Select emoji reaction')
  }
}

const handleQuoteClick = (messageId: string | number, matrixEventId?: string) => {
  emit('quote-click', messageId, matrixEventId)
}

const handleAvatarClick = (userId: string | number) => {
  emit('avatar-click', userId)
}

const {
  handleReply,
  handleEdit,
  handleProfile,
  handleSystemMessageClick,
  isDeleteMessageDialogOpen,
  isDeletingMessage,
  handleDelete,
  confirmDeleteMessage,
  cancelDeleteMessage,
  isPinDialogOpen,
  pinForPeer,
  handlePinToggle,
  confirmPinMessage,
  cancelPinMessage,
  handlePinnedSelect,
  handlePinnedUnpin,
  isForwardDialogOpen,
  forwardDialogRef,
  pendingForwardCount,
  handleForward,
  handleSelectMessage,
  openForwardSelected,
  cancelForwardMessage,
  confirmForwardMessage,
  isSelectionMode,
  selectedCount,
  isMessageSelected,
  toggleMessageSelection,
  clearMessageSelection,
} = useMessageActions({
  messages,
  chatId,
  getMessageId,
  closeMessageMenu,
  canPinMessages,
  canForwardFromActiveChat,
  isPinDialogMode,
  pinActorName,
  isPinned,
  pinMessage,
  unpinMessage,
  onReply: (message, senderId) => emit('reply', message, senderId),
  onStartEdit: (messageId, msgData) => emit('start-edit', messageId, msgData),
  onOpenProfile: userId => emit('avatar-click', userId),
  onQuoteClick: (messageId, matrixEventId) => emit('quote-click', messageId, matrixEventId),
})

watch(
  [isSelectionMode, selectedCount],
  ([active, count]) => {
    emit('selection-change', { active, count })
    if (active) {
      closeMessageMenu()
    }
  },
)

defineExpose({
  clearMessageSelection,
  openForwardSelected,
})

const currentMessage = computed(() => {
  if (!showMenuId.value) return null
  for (const item of groupedMessages.value) {
    if (item.kind === 'group') {
      for (const msg of item.group.messages) {
        const msgId = getMessageId(msg, item.group.senderId)
        if (String(msgId) === String(showMenuId.value)) {
          interface MessageWithSenderId {
            senderId?: number | string
            sender_id?: number | string
            [key: string]: unknown
          }
          const typedMsg = msg as MessageWithSenderId

          return {
            ...msg,
            senderId:
              typedMsg.senderId || typedMsg.sender_id || item.group.senderId,
          }
        }
      }
    }
  }

  return null
})

const chatLogRef = ref<HTMLElement | null>(null)

usePhotoSwipe({
  gallery: chatLogRef,
  children: 'a.pswp-gallery',
})

const reactionContext: ChatReactionContext = {
  onTriggerEnter,
  onTriggerClick,
  onTriggerLeave,
  onReactionPillClick: handleReactionClick,
}

provide(chatReactionContextKey, reactionContext)

onMounted(async () => {
  await chatStoreCore.fetchReactionTypes()
})
</script>

<template>
  <div
    ref="chatLogRef"
    class="chat-log-root"
  >
    <PinnedMessagesBar
      :messages="pinnedMessages"
      :can-unpin="canPinMessages"
      :busy="isPinning"
      @select="handlePinnedSelect"
      @unpin="handlePinnedUnpin"
    />

    <div
      v-if="loadingOlderHistory"
      class="chat-older-history-loader"
      :class="{ 'chat-older-history-loader--below-pin': pinnedMessages.length > 0 }"
      aria-live="polite"
    >
      <div class="chat-older-history-loader__pill">
        <VProgressCircular
          indeterminate
          size="16"
          width="2"
          color="primary"
        />
        <span>Загрузка сообщений…</span>
      </div>
    </div>

    <div class="chat-log pa-5">
      <ChatLogSkeleton v-if="store.isLoadingChat" />

      <div
        v-if="groupedMessages.length > 0"
        :class="{ 'chat-messages-hidden': store.isLoadingChat }"
      >
        <template
          v-for="(item, index) in groupedMessages"
          :key="
            item.kind === 'date'
              ? `date-${item.dateKey}`
              : item.kind === 'system'
                ? `sys-${item.message.matrix_event_id || item.message.id || index}`
                : `grp-${item.group.messages[0]?.matrix_event_id
                  || item.group.messages[0]?.id
                  || item.group.senderId
                  || index}`
          "
        >
          <DateSeparator v-if="item.kind === 'date'" :label="item.label" />

          <button
            v-else-if="item.kind === 'system'"
            type="button"
            class="chat-system-message"
            @click="handleSystemMessageClick(item.message)"
          >
            {{ item.message.message }}
          </button>

          <div v-else class="message-group-container">
            <MessageGroup
              :item="item"
              :is-mine="isMine"
              :is-pinned="isPinned"
              :contact="contact"
              :profile-user="profileUser"
              :hovered-message-id="hoveredMessageId"
              :get-message-id="getMessageId"
              :get-grouped-reactions="getGroupedReactions"
              :get-reply-sender-name="getReplySenderName"
              :chat-id="chatId"
              :chat-type="chatType"
              :can-pin="canPinMessages"
              :can-forward="canForwardFromActiveChat"
              :can-reply="canReplyInActiveChat"
              :can-open-profile="canOpenProfileInActiveChat"
              :selection-mode="isSelectionMode"
              :is-message-selected="isMessageSelected"
              :can-select-message="canSelectMessageForForward"
              :is-last-message-in-chat="
                isLastMessageInChat(item, index, groupedMessages)
              "
              class="mb-8"
              @message-enter="onMessageEnter"
              @message-leave="onMessageLeave"
              @reaction-click="handleReactionClick"
              @trigger-enter="onTriggerEnter"
              @trigger-leave="onTriggerLeave"
              @trigger-click="onTriggerClick"
              @menu-click="openMessageMenu"
              @quote-click="handleQuoteClick"
              @avatar-click="handleAvatarClick"
              @toggle-select="toggleMessageSelection"
            />
          </div>
        </template>
      </div>

      <ReactionPicker
        v-if="!isSelectionMode"
        :model-value="showPickerId !== null && pickerPosition !== null"
        :position="pickerPosition"
        :active-emoji="pickerActiveEmoji"
        @update:model-value="showPickerId = $event ? showPickerId : null"
        @select-emoji="(emoji: string) => {
          const id = showPickerId
          if (id != null) selectEmojiReaction(id, emoji)
        }"
        @mouseenter="onPickerEnter"
        @mouseleave="onPickerLeave"
      />

      <MessageMenu
        v-if="!isSelectionMode && showMenuId && currentMessage && menuPosition"
        :model-value="showMenuId !== null"
        :position="menuPosition"
        :message="currentMessage"
        :is-mine="isMine(currentMessage.senderId)"
        :can-pin="canPinMessages"
        :is-pinned="isPinned(currentMessage.matrix_event_id)"
        :can-forward="canForwardFromActiveChat"
        :can-reply="canReplyInActiveChat"
        :can-open-profile="canOpenProfileInActiveChat"
        @update:model-value="showMenuId = $event ? showMenuId : null"
        @reply="handleReply(currentMessage, currentMessage.senderId)"
        @edit="handleEdit(currentMessage, currentMessage.senderId)"
        @delete="handleDelete(currentMessage, currentMessage.senderId)"
        @profile="handleProfile(currentMessage, currentMessage.senderId)"
        @pin="handlePinToggle(currentMessage)"
        @forward="handleForward(currentMessage)"
        @select="handleSelectMessage(currentMessage)"
      />

      <ConfirmChatDialog
        v-model="isDeleteMessageDialogOpen"
        title="Удалить сообщение?"
        text="Сообщение будет удалено у всех"
        confirm-text="Удалить"
        color="error"
        :loading="isDeletingMessage"
        @confirm="confirmDeleteMessage"
        @cancel="cancelDeleteMessage"
      />

      <ConfirmChatDialog
        v-model="isPinDialogOpen"
        title="Закрепить сообщение"
        text="Закрепить это сообщение в чате?"
        confirm-text="Закрепить"
        cancel-text="Отмена"
        :loading="isPinning"
        @confirm="confirmPinMessage"
        @cancel="cancelPinMessage"
      >
        <VCheckbox
          v-model="pinForPeer"
          :label="`Также закрепить для ${pinPeerName.trim() || 'собеседника'}`"
          density="compact"
          hide-details
          class="px-4 mb-2"
        />
      </ConfirmChatDialog>

      <ForwardMessageDialog
        ref="forwardDialogRef"
        v-model="isForwardDialogOpen"
        :message-count="pendingForwardCount"
        @confirm="confirmForwardMessage"
        @cancel="cancelForwardMessage"
      />

      <MessageReaders
        v-if="shouldShowReaders && lastMessageInChat"
        :key="`readers-${matrixRoomId}-${lastMessageInChat.matrixEventId}`"
        :matrix-room-id="matrixRoomId"
        :matrix-event-id="lastMessageInChat.matrixEventId"
        :is-mine="isMine(lastMessageInChat.senderId)"
        :is-seen="Boolean(lastMessageInChat.message.feedback?.isSeen)"
      />
    </div>
  </div>
</template>

<style lang="scss" src="@/modules/chat-matrix-fast/styles/chat-log.scss"></style>
